import { useEffect, useMemo, useState } from 'react'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { Modal, ModalFooterWithActions, TabbedModal } from '@/components/modal'
import { BrowseMatterFiles } from '@/user_files/components/BrowseMatterFiles'
import { useBrowseFiles } from '@/user_files/components/useBrowseFiles'
import { kGetFeatureFlagStatus, kFeatureFlags } from '@/constants/constants-feature-flags'
import type { SelectedTemplate } from './types'
import { useExternalFilesTab } from '@/user_files/external-storage/hooks/useExternalFilesTab'
import { filterLocalStorageItems } from '@/user_files/external-storage/utils/external-storage-items'

export const SELECT_TEMPLATE_MODAL_TAB = {
  MATTER_FILES: 'matter-files',
  EXTERNAL_FILES: 'external-files',
} as const

const TAB_MATTER = SELECT_TEMPLATE_MODAL_TAB.MATTER_FILES
const TAB_TEMPLATES = 'templates'
const TAB_EXTERNAL = SELECT_TEMPLATE_MODAL_TAB.EXTERNAL_FILES
export type SelectTemplateModalInitialTab = (typeof SELECT_TEMPLATE_MODAL_TAB)[keyof typeof SELECT_TEMPLATE_MODAL_TAB]

export type SelectTemplateModalProps = {
  /** Whether the modal is open. */
  open: boolean
  /** The matter to browse for template files. */
  matterId: string
  /** The currently selected template, used to pre-select on open. */
  template?: SelectedTemplate
  /** Called when the modal is closed. */
  onClose: () => void
  /** Called with the selected template on confirm, or undefined to clear. */
  onSelect: (template?: SelectedTemplate) => void
  /** Optional tab to show first when the modal opens. */
  initialTab?: SelectTemplateModalInitialTab
}

type TemplateSelection = {
  selectedFileIds: string[]
  template?: SelectedTemplate
}

function isDocxFile(file: UserFileRead): boolean {
  if (file.detected_content_type !== null) {
    return file.detected_content_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  }
  return file.original_filename.toLowerCase().endsWith('.docx')
}

export function SelectTemplateModal({
  open,
  matterId,
  template,
  onClose,
  onSelect,
  initialTab = SELECT_TEMPLATE_MODAL_TAB.MATTER_FILES,
}: SelectTemplateModalProps) {
  const [activeTabId, setActiveTabId] = useState<string>(TAB_MATTER)
  const [selection, setSelection] = useState<TemplateSelection>({ selectedFileIds: [], template: undefined })

  const matterFilesBrowse = useBrowseFiles({
    matterId,
    open,
  })

  const externalFilesTab = useExternalFilesTab({
    matterId,
    open,
    active: activeTabId === TAB_EXTERNAL,
    fallbackBreadcrumbItems: matterFilesBrowse.breadcrumbItems,
  })
  const showExternalTab = externalFilesTab.showExternalTab

  useEffect(() => {
    if (!open) return
    setActiveTabId(initialTab)
    if (template?.fileId) {
      setSelection({ selectedFileIds: [template.fileId], template: template })
    } else {
      setSelection({ selectedFileIds: [], template: undefined })
    }
  }, [initialTab, open, template])

  useEffect(() => {
    if (activeTabId === TAB_EXTERNAL && !externalFilesTab.externalSyncStatus.loading && !showExternalTab) {
      setActiveTabId(TAB_MATTER)
    }
  }, [activeTabId, externalFilesTab.externalSyncStatus.loading, showExternalTab])

  const localFolders = useMemo(() => filterLocalStorageItems(matterFilesBrowse.folders), [matterFilesBrowse.folders])
  const localFiles = useMemo(() => filterLocalStorageItems(matterFilesBrowse.files), [matterFilesBrowse.files])

  const localDocxFiles = useMemo(() => localFiles.filter(isDocxFile), [localFiles])
  const externalDocxFiles = useMemo(() => externalFilesTab.externalFiles.filter(isDocxFile), [externalFilesTab.externalFiles])

  const handleToggleFile = (file: UserFileRead) => {
    const fileId = file.id
    setSelection((s) => {
      if (s.selectedFileIds[0] === fileId) {
        return { selectedFileIds: [], template: undefined }
      }
      return { selectedFileIds: [fileId], template: { fileId, name: file.original_filename } }
    })
  }

  const handleClear = () => setSelection({ selectedFileIds: [], template: undefined })

  const handleDone = () => {
    onSelect(selection.template)
    onClose()
  }

  const isSelectionTab = activeTabId === TAB_MATTER || activeTabId === TAB_EXTERNAL

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose()
      }}
    >
      <TabbedModal
        title="Select template"
        width="lg"
        height="md"
        showCloseButton
        activeTabId={activeTabId}
        onTabChange={setActiveTabId}
        footer={
          <ModalFooterWithActions
            tertiaryText={selection.template ? `"${selection.template.name}" selected` : undefined}
            secondaryAction={{
              label: 'Clear',
              onClick: handleClear,
              disabled: !isSelectionTab || !selection.template,
            }}
            primaryAction={{
              label: 'Done',
              onClick: handleDone,
              disabled: !isSelectionTab || (!selection.template && !template),
            }}
          />
        }
        tabs={[
          {
            id: TAB_MATTER,
            label: 'Matter Files',
            panel: (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <BrowseMatterFiles
                  breadcrumbItems={matterFilesBrowse.breadcrumbItems}
                  blockingError={matterFilesBrowse.error}
                  files={localDocxFiles}
                  folders={localFolders}
                  loading={matterFilesBrowse.loading}
                  onNavigateToFolder={matterFilesBrowse.onNavigateToFolder}
                  onRetry={matterFilesBrowse.retry}
                  onToggleFile={handleToggleFile}
                  selectedFileIds={selection.selectedFileIds}
                  selectionVariant="radio"
                />
              </div>
            ),
          },
          ...(kGetFeatureFlagStatus(kFeatureFlags.TEMPLATES_TAB)
            ? [
                {
                  id: TAB_TEMPLATES,
                  label: 'Templates',
                  panel: (
                    <div className="flex min-h-[240px] flex-col items-center justify-center px-6 py-10 text-center text-sm text-brand-neutral-500">
                      <p>Templates are not available yet.</p>
                    </div>
                  ),
                },
              ]
            : []),
          ...(showExternalTab
            ? [
                {
                  id: TAB_EXTERNAL,
                  label: externalFilesTab.syncedProviderLabel ?? '',
                  panel: (
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                      <BrowseMatterFiles
                        {...externalFilesTab.browseProps}
                        files={externalFilesTab.activeExternalMapping ? externalDocxFiles : []}
                        onToggleFile={handleToggleFile}
                        selectedFileIds={selection.selectedFileIds}
                        selectionVariant="radio"
                      />
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
    </Modal>
  )
}
